import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Users, Search, Phone, Mail, MapPin, X, MessageSquare, Edit2, Trash2, Calculator, CheckCircle2 } from 'lucide-react';
import { getLeads, createLead, updateLead, addLeadNote, deleteLead, getLeadStats } from '../utils/api';
import { toast } from 'sonner';
import axios from 'axios';
import AgentLayout from '../components/AgentLayout';
import AdvancedQuoteFields, { leadToQuoteData, buildQuotePayload, getQuotes, quoteCount } from '../components/AdvancedQuoteFields';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COVERAGE_MAP = { whole_life: 'life', term: 'life', iul: 'life', annuity: 'life', final_expense: 'final_expense', medicare: 'medicare', health: 'health' };

const statusColors = {
  new: 'bg-blue-100 text-blue-800',
  contacted: 'bg-yellow-100 text-yellow-800',
  qualified: 'bg-purple-100 text-purple-800',
  proposal: 'bg-indigo-100 text-indigo-800',
  closed_won: 'bg-green-100 text-green-800',
  closed_lost: 'bg-red-100 text-red-800',
};

const statusLabels = {
  new: 'New', contacted: 'Contacted', qualified: 'Qualified',
  proposal: 'Proposal', closed_won: 'Closed Won', closed_lost: 'Closed Lost',
};

const coverageLabels = {
  final_expense: 'Final Expense', medicare: 'Medicare',
  life: 'Life Insurance', health: 'Health & Supplemental',
};

const sourceLabels = {
  web: 'Website', referral: 'Referral', purchased: 'Purchased',
  door_knock: 'Door Knock', phone: 'Phone', other: 'Other',
};

const formTypeColors = {
  'Quote': 'bg-green-100 text-green-800',
  'Contact': 'bg-blue-100 text-blue-800',
  'Career Interest': 'bg-orange-100 text-orange-800',
  'Newsletter': 'bg-purple-100 text-purple-800',
  'Invite Sent': 'bg-teal-100 text-teal-800',
};

const LeadManagement = () => {
  const navigate = useNavigate();
  const [agentInfo, setAgentInfo] = useState(null);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formTypeFilter, setFormTypeFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editDraft, setEditDraft] = useState({});
  const [quoteEditMode, setQuoteEditMode] = useState(false);
  const [quoteDraft, setQuoteDraft] = useState({});
  const [quoteEditIndex, setQuoteEditIndex] = useState(null);
  const [newLead, setNewLead] = useState({
    firstName: '', lastName: '', email: '', phone: '', zipCode: '',
    coverageInterest: 'final_expense', source: 'web', notes: '', assignedTo: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('agentToken');
    const info = localStorage.getItem('agentInfo');
    if (!token || !info) { navigate('/agent/login'); return; }
    const init = async () => {
      setAgentInfo(JSON.parse(info));
      await loadData(token);
    };
    init();
  }, [navigate]);

  const rev = (arr) => (arr || []).reduceRight((acc, x) => { acc.push(x); return acc; }, []);

  const loadData = async (token) => {
    try {
      setIsLoading(true);
      const [leadsRes, statsRes] = await Promise.all([
        getLeads(token),
        getLeadStats(token)
      ]);
      if (leadsRes.success) setLeads(leadsRes.leads);
      if (statsRes.success) setStats(statsRes.stats);

      // Load agents for assignment dropdown (admin only)
      try {
        const agentsRes = await axios.get(`${API}/admin/agents`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (agentsRes.data.agents) setAgents(agentsRes.data.agents);
      } catch (e) { /* non-admin */ }
    } catch (error) {
      console.error('Error loading leads:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateLead = async () => {
    if (!newLead.firstName || !newLead.lastName || !newLead.phone) {
      toast.error('First name, last name, and phone are required');
      return;
    }
    const token = localStorage.getItem('agentToken');
    try {
      const result = await createLead({ ...newLead }, token);
      if (result.success) {
        toast.success('Lead created successfully');
        setShowCreateModal(false);
        setNewLead({ firstName: '', lastName: '', email: '', phone: '', zipCode: '', coverageInterest: 'final_expense', source: 'web', notes: '', assignedTo: '' });
        loadData(token);
      }
    } catch (error) {
      toast.error('Failed to create lead');
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    const token = localStorage.getItem('agentToken');
    try {
      await updateLead(leadId, { status: newStatus }, token);
      toast.success(`Status updated to ${statusLabels[newStatus]}`);
      loadData(token);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAssignChange = async (leadId, agentId) => {
    const token = localStorage.getItem('agentToken');
    try {
      await updateLead(leadId, { assignedTo: agentId || null }, token);
      toast.success('Lead reassigned');
      loadData(token);
    } catch (error) {
      toast.error('Failed to assign lead');
    }
  };

  const handleAddNote = async (leadId) => {
    if (!noteText.trim()) return;
    const token = localStorage.getItem('agentToken');
    try {
      await addLeadNote(leadId, noteText, token);
      toast.success('Note added');
      setNoteText('');
      loadData(token);
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Delete this lead permanently?')) return;
    const token = localStorage.getItem('agentToken');
    try {
      await deleteLead(leadId, token);
      toast.success('Lead deleted');
      setShowDetailModal(null);
      loadData(token);
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const startEdit = () => {
    setEditDraft({
      firstName: showDetailModal.firstName || '', lastName: showDetailModal.lastName || '',
      email: showDetailModal.email || '', phone: showDetailModal.phone || '',
      zipCode: showDetailModal.zipCode || '', coverageInterest: showDetailModal.coverageInterest || 'final_expense',
      source: showDetailModal.source || 'web', formType: showDetailModal.formType || 'Manual',
      status: showDetailModal.status || 'new',
    });
    setEditMode(true);
  };

  const saveEdit = async () => {
    const token = localStorage.getItem('agentToken');
    try {
      await updateLead(showDetailModal.id, editDraft, token);
      toast.success('Contact updated');
      setShowDetailModal({ ...showDetailModal, ...editDraft });
      setEditMode(false);
      loadData(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update contact');
    }
  };

  const startQuoteEdit = (index) => {
    // index = existing quote index to edit, or the length to add a new quote
    const quotes = getQuotes(showDetailModal);
    const quoteObj = (index != null && index < quotes.length) ? quotes[index] : {};
    setQuoteEditIndex(index != null ? index : quotes.length);
    setQuoteDraft(leadToQuoteData(showDetailModal, quoteObj));
    setQuoteEditMode(true);
  };

  const saveQuoteDetails = async () => {
    const token = localStorage.getItem('agentToken');
    const payload = buildQuotePayload(quoteDraft);
    const quotes = [...getQuotes(showDetailModal)];
    const idx = quoteEditIndex != null ? quoteEditIndex : quotes.length;
    quotes[idx] = payload.quoteDetails;
    const update = {
      firstName: payload.firstName || showDetailModal.firstName,
      lastName: payload.lastName || showDetailModal.lastName,
      email: payload.email,
      phone: payload.phone || showDetailModal.phone,
      streetAddress: payload.streetAddress,
      city: payload.city,
      state: payload.state,
      zipCode: payload.zipCode,
      quotes,
      quoteDetails: payload.quoteDetails,
    };
    if (quoteDraft.coverageType) update.coverageInterest = COVERAGE_MAP[quoteDraft.coverageType] || 'life';
    try {
      await updateLead(showDetailModal.id, update, token);
      toast.success(quotes.length > 1 ? `Quote #${idx + 1} saved` : 'Quote details saved');
      setShowDetailModal({ ...showDetailModal, ...update });
      setQuoteEditMode(false);
      loadData(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save quote details');
    }
  };

  const filtered = leads.filter(l => {
    if (statusFilter !== 'all' && l.status !== statusFilter) return false;
    if (formTypeFilter !== 'all' && (l.formType || 'Manual') !== formTypeFilter) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (l.firstName + ' ' + l.lastName).toLowerCase().includes(term) ||
        l.phone?.includes(term) || l.email?.toLowerCase().includes(term);
    }
    return true;
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue mx-auto"></div>
      </div>
    );
  }

  return (
    <AgentLayout title="Lead Management">
      <div data-testid="lead-management-page">

      <div>
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6" data-testid="lead-stats">
            <div className="bg-white rounded-lg shadow p-3 text-center"><p className="text-2xl font-bold text-gray-800">{stats.total}</p><p className="text-xs text-gray-500">Total</p></div>
            <div className="bg-white rounded-lg shadow p-3 text-center"><p className="text-2xl font-bold text-blue-600">{stats.new}</p><p className="text-xs text-gray-500">New</p></div>
            <div className="bg-white rounded-lg shadow p-3 text-center"><p className="text-2xl font-bold text-yellow-600">{stats.contacted}</p><p className="text-xs text-gray-500">Contacted</p></div>
            <div className="bg-white rounded-lg shadow p-3 text-center"><p className="text-2xl font-bold text-purple-600">{stats.qualified}</p><p className="text-xs text-gray-500">Qualified</p></div>
            <div className="bg-white rounded-lg shadow p-3 text-center"><p className="text-2xl font-bold text-indigo-600">{stats.proposal}</p><p className="text-xs text-gray-500">Proposal</p></div>
            <div className="bg-white rounded-lg shadow p-3 text-center"><p className="text-2xl font-bold text-green-600">{stats.closed_won}</p><p className="text-xs text-gray-500">Won</p></div>
            <div className="bg-white rounded-lg shadow p-3 text-center"><p className="text-2xl font-bold text-red-600">{stats.closed_lost}</p><p className="text-xs text-gray-500">Lost</p></div>
            <div onClick={() => setFormTypeFilter(f => f === 'Invite Sent' ? 'all' : 'Invite Sent')} className={`bg-teal-50 border rounded-lg shadow p-3 text-center cursor-pointer transition hover:shadow-md hover:border-teal-400 ${formTypeFilter === 'Invite Sent' ? 'border-teal-500 ring-2 ring-teal-300' : 'border-teal-200'}`} data-testid="lead-stat-invites" title="Click to view prospects you've invited"><p className="text-2xl font-bold text-teal-700">{stats.invitesSent || 0}</p><p className="text-xs text-teal-600 font-medium">Invites Sent</p><p className="text-[10px] text-gray-400">{stats.invitesSentThisWeek || 0} this week</p></div>
          </div>
        )}

        {/* Filters + Create */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6 flex flex-wrap gap-3 items-center" data-testid="lead-filters">
          <div className="flex-1 relative min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search leads..." className="w-full pl-10 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-teal-500 focus:outline-none" data-testid="lead-search" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 border rounded-md focus:ring-2 focus:ring-teal-500 focus:outline-none" data-testid="lead-status-filter">
            <option value="all">All Status</option>
            {Object.entries(statusLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={formTypeFilter} onChange={(e) => setFormTypeFilter(e.target.value)} className="px-3 py-2 border rounded-md focus:ring-2 focus:ring-teal-500 focus:outline-none" data-testid="lead-formtype-filter">
            <option value="all">All Form Types</option>
            <option value="Quote">Quote</option>
            <option value="Contact">Contact</option>
            <option value="Career Interest">Career Interest</option>
            <option value="Newsletter">Newsletter</option>
            <option value="Manual">Manual</option>
          </select>
          <button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700 transition font-semibold" data-testid="create-lead-btn">
            <Plus size={18} /> New Lead
          </button>
        </div>

        {/* Leads Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden" data-testid="leads-table">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left font-bold">Name</th>
                <th className="px-4 py-3 text-left font-bold">Contact</th>
                <th className="px-4 py-3 text-center font-bold">Form Type</th>
                <th className="px-4 py-3 text-center font-bold">Coverage</th>
                <th className="px-4 py-3 text-center font-bold">Source</th>
                <th className="px-4 py-3 text-center font-bold">Status</th>
                <th className="px-4 py-3 text-center font-bold">Assigned To</th>
                <th className="px-4 py-3 text-center font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="8" className="px-4 py-8 text-center text-gray-400">No leads found</td></tr>
              ) : filtered.map((lead) => (
                <tr key={lead.id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => { setShowDetailModal(lead); setEditMode(false); setQuoteEditMode(false); }} data-testid={`lead-row-${lead.id}`}>
                  <td className="px-4 py-3 font-semibold">
                    <div className="flex flex-col gap-1">
                      <span>{lead.firstName} {lead.lastName}</span>
                      {(() => {
                        const c = quoteCount(lead);
                        if (c >= 2) return <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full w-fit" data-testid={`quote-badge-${lead.id}`}><CheckCircle2 size={11} /> {c} quotes</span>;
                        if (c === 1) return <span className="inline-flex items-center gap-1 text-[10px] font-bold text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full w-fit" data-testid={`quote-badge-${lead.id}`}><CheckCircle2 size={11} /> Quote details</span>;
                        return <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full w-fit" data-testid={`quote-badge-${lead.id}`}>No details yet</span>;
                      })()}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-0.5">
                      <span className="flex items-center gap-1 text-xs"><Phone size={12} />{lead.phone}</span>
                      {lead.email && <span className="flex items-center gap-1 text-xs"><Mail size={12} />{lead.email}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${formTypeColors[lead.formType] || 'bg-gray-100 text-gray-700'}`} data-testid={`lead-formtype-${lead.id}`}>{lead.formType || 'Manual'}</span>
                  </td>
                  <td className="px-4 py-3 text-center text-xs">{coverageLabels[lead.coverageInterest] || lead.coverageInterest}</td>
                  <td className="px-4 py-3 text-center text-xs">{sourceLabels[lead.source] || lead.source}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusColors[lead.status] || 'bg-gray-100 text-gray-800'}`}>{statusLabels[lead.status] || lead.status}</span>
                  </td>
                  <td className="px-4 py-3 text-center text-xs">{lead.assignedToName || 'Unassigned'}</td>
                  <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                    <select value={lead.status} onChange={(e) => handleStatusChange(lead.id, e.target.value)} className="text-xs px-2 py-1 border rounded" data-testid={`lead-status-select-${lead.id}`}>
                      {Object.entries(statusLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      {/* Create Lead Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center" data-testid="create-lead-modal">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">Create New Lead</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">First Name *</label>
                <input type="text" value={newLead.firstName} onChange={(e) => setNewLead({...newLead, firstName: e.target.value})} className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-teal-500 focus:outline-none" data-testid="lead-first-name" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Last Name *</label>
                <input type="text" value={newLead.lastName} onChange={(e) => setNewLead({...newLead, lastName: e.target.value})} className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-teal-500 focus:outline-none" data-testid="lead-last-name" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Phone *</label>
                <input type="tel" value={newLead.phone} onChange={(e) => setNewLead({...newLead, phone: e.target.value})} className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-teal-500 focus:outline-none" data-testid="lead-phone" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Email</label>
                <input type="email" value={newLead.email} onChange={(e) => setNewLead({...newLead, email: e.target.value})} className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-teal-500 focus:outline-none" data-testid="lead-email" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Zip Code</label>
                <input type="text" value={newLead.zipCode} onChange={(e) => setNewLead({...newLead, zipCode: e.target.value})} className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-teal-500 focus:outline-none" data-testid="lead-zip" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Coverage Interest</label>
                <select value={newLead.coverageInterest} onChange={(e) => setNewLead({...newLead, coverageInterest: e.target.value})} className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-teal-500 focus:outline-none" data-testid="lead-coverage">
                  {Object.entries(coverageLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Source</label>
                <select value={newLead.source} onChange={(e) => setNewLead({...newLead, source: e.target.value})} className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-teal-500 focus:outline-none" data-testid="lead-source">
                  {Object.entries(sourceLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              {agents.length > 0 && (
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Assign To</label>
                  <select value={newLead.assignedTo} onChange={(e) => setNewLead({...newLead, assignedTo: e.target.value})} className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-teal-500 focus:outline-none" data-testid="lead-assign">
                    <option value="">Unassigned</option>
                    {agents.map(a => <option key={a.id} value={a.id}>{a.fullName}</option>)}
                  </select>
                </div>
              )}
              <div className="col-span-2">
                <label className="block text-xs font-bold text-gray-700 mb-1">Notes</label>
                <textarea value={newLead.notes} onChange={(e) => setNewLead({...newLead, notes: e.target.value})} rows={2} className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-teal-500 focus:outline-none" data-testid="lead-notes" />
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-4">
              <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-semibold">Cancel</button>
              <button onClick={handleCreateLead} className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 font-semibold" data-testid="save-lead-btn">Create Lead</button>
            </div>
          </div>
        </div>
      )}

      {/* Lead Detail Modal */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center" data-testid="lead-detail-modal">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">{showDetailModal.firstName} {showDetailModal.lastName}</h3>
              <div className="flex items-center gap-2">
                {!editMode && (
                  <button onClick={startEdit} className="flex items-center gap-1 px-3 py-1.5 bg-brand-blue text-white rounded-md text-sm font-semibold hover:bg-brand-orange" data-testid="edit-lead-btn"><Edit2 size={14} /> Edit</button>
                )}
                <button onClick={() => { setShowDetailModal(null); setEditMode(false); setQuoteEditMode(false); }} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
              </div>
            </div>

            {editMode ? (
              <div className="grid grid-cols-2 gap-3 mb-4" data-testid="lead-edit-form">
                <div><label className="block text-xs font-bold text-gray-700 mb-1">First Name</label><input value={editDraft.firstName} onChange={(e) => setEditDraft({...editDraft, firstName: e.target.value})} className="w-full px-3 py-2 border rounded-md text-sm" data-testid="edit-first-name" /></div>
                <div><label className="block text-xs font-bold text-gray-700 mb-1">Last Name</label><input value={editDraft.lastName} onChange={(e) => setEditDraft({...editDraft, lastName: e.target.value})} className="w-full px-3 py-2 border rounded-md text-sm" data-testid="edit-last-name" /></div>
                <div><label className="block text-xs font-bold text-gray-700 mb-1">Phone</label><input value={editDraft.phone} onChange={(e) => setEditDraft({...editDraft, phone: e.target.value})} className="w-full px-3 py-2 border rounded-md text-sm" data-testid="edit-phone" /></div>
                <div><label className="block text-xs font-bold text-gray-700 mb-1">Email</label><input value={editDraft.email} onChange={(e) => setEditDraft({...editDraft, email: e.target.value})} className="w-full px-3 py-2 border rounded-md text-sm" data-testid="edit-email" /></div>
                <div><label className="block text-xs font-bold text-gray-700 mb-1">Zip Code</label><input value={editDraft.zipCode} onChange={(e) => setEditDraft({...editDraft, zipCode: e.target.value})} className="w-full px-3 py-2 border rounded-md text-sm" data-testid="edit-zip" /></div>
                <div><label className="block text-xs font-bold text-gray-700 mb-1">Type</label>
                  <select value={editDraft.formType} onChange={(e) => setEditDraft({...editDraft, formType: e.target.value})} className="w-full px-3 py-2 border rounded-md text-sm" data-testid="edit-formtype">
                    <option value="Quote">Quote</option><option value="Contact">Contact</option><option value="Career Interest">Career Interest</option><option value="Newsletter">Newsletter</option><option value="Manual">Manual</option>
                  </select>
                </div>
                <div><label className="block text-xs font-bold text-gray-700 mb-1">Coverage Interest</label>
                  <select value={editDraft.coverageInterest} onChange={(e) => setEditDraft({...editDraft, coverageInterest: e.target.value})} className="w-full px-3 py-2 border rounded-md text-sm" data-testid="edit-coverage">
                    {Object.entries(coverageLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div><label className="block text-xs font-bold text-gray-700 mb-1">Source</label>
                  <select value={editDraft.source} onChange={(e) => setEditDraft({...editDraft, source: e.target.value})} className="w-full px-3 py-2 border rounded-md text-sm" data-testid="edit-source">
                    {Object.entries(sourceLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div><label className="block text-xs font-bold text-gray-700 mb-1">Status</label>
                  <select value={editDraft.status} onChange={(e) => setEditDraft({...editDraft, status: e.target.value})} className="w-full px-3 py-2 border rounded-md text-sm" data-testid="edit-status">
                    {Object.entries(statusLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="col-span-2 flex gap-2 justify-end">
                  <button onClick={() => setEditMode(false)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm font-semibold">Cancel</button>
                  <button onClick={saveEdit} className="px-4 py-2 bg-teal-600 text-white rounded-md text-sm font-semibold hover:bg-teal-700" data-testid="save-edit-btn">Save Changes</button>
                </div>
              </div>
            ) : (
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div><p className="text-xs text-gray-500">Phone</p><p className="font-semibold flex items-center gap-1"><Phone size={14} />{showDetailModal.phone}</p></div>
              <div><p className="text-xs text-gray-500">Email</p><p className="font-semibold flex items-center gap-1"><Mail size={14} />{showDetailModal.email || 'N/A'}</p></div>
              <div><p className="text-xs text-gray-500">Zip Code</p><p className="font-semibold flex items-center gap-1"><MapPin size={14} />{showDetailModal.zipCode || 'N/A'}</p></div>
              <div><p className="text-xs text-gray-500">Form Type</p><p className="font-semibold">{showDetailModal.formType || 'Manual'}</p></div>
              <div><p className="text-xs text-gray-500">Coverage Interest</p><p className="font-semibold">{coverageLabels[showDetailModal.coverageInterest] || showDetailModal.coverageInterest}</p></div>
              <div><p className="text-xs text-gray-500">Source</p><p className="font-semibold">{sourceLabels[showDetailModal.source]}</p></div>
              <div><p className="text-xs text-gray-500">Status</p>
                <select value={showDetailModal.status} onChange={(e) => { handleStatusChange(showDetailModal.id, e.target.value); setShowDetailModal({...showDetailModal, status: e.target.value}); }} className="px-2 py-1 border rounded text-sm" data-testid="detail-status-select">
                  {Object.entries(statusLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              {agents.length > 0 && (
                <div className="col-span-2"><p className="text-xs text-gray-500">Assigned To</p>
                  <select value={showDetailModal.assignedTo || ''} onChange={(e) => { handleAssignChange(showDetailModal.id, e.target.value); setShowDetailModal({...showDetailModal, assignedTo: e.target.value}); }} className="px-2 py-1 border rounded text-sm" data-testid="detail-assign-select">
                    <option value="">Unassigned</option>
                    {agents.map(a => <option key={a.id} value={a.id}>{a.fullName}</option>)}
                  </select>
                </div>
              )}
            </div>
            )}

            {/* Advanced Quote Details */}
            <div className="border-t pt-4" data-testid="lead-quote-details">
              {(() => {
                const quotes = getQuotes(showDetailModal);
                const ageDob = (x) => {
                  const parts = [];
                  if (x?.age) parts.push(`Age ${x.age}`);
                  if (x?.dob) parts.push(`DOB ${x.dob}`);
                  return parts.join(' / ') || '—';
                };
                const relLabel = (x) => {
                  const r = x?.relationship || 'self';
                  return r === 'other' ? (x?.relationshipOther || 'Other') : r.charAt(0).toUpperCase() + r.slice(1);
                };
                const hw = (x) => `${(x?.heightFt || x?.heightIn) ? `${x.heightFt || 0} ft ${x.heightIn || 0} in` : '—'}`;
                const renderQuote = (q, idx) => {
                  const p = q.primary || {};
                  return (
                    <div key={idx} className="border border-gray-200 rounded-lg p-3" data-testid={`quote-block-${idx}`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-bold text-sm text-brand-blue">Quote #{idx + 1} Request</p>
                        <button onClick={() => startQuoteEdit(idx)} className="text-xs font-semibold text-brand-blue hover:text-brand-orange flex items-center gap-1" data-testid={`edit-quote-${idx}`}><Calculator size={13} /> Edit</button>
                      </div>
                      <div className="space-y-3 text-sm">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          <div><p className="text-xs text-gray-500">Coverage Type</p><p className="font-semibold capitalize">{(q.coverageType || '—').replace('_', ' ')}</p></div>
                          <div><p className="text-xs text-gray-500">Desired Coverage Amount</p><p className="font-semibold">{q.coverageAmount || '—'}</p></div>
                          <div><p className="text-xs text-gray-500">Est. Monthly Budget</p><p className="font-semibold">{q.monthlyBudget || '—'}</p></div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs font-bold text-gray-700 mb-1">Primary Insured <span className="font-normal text-gray-500">· {relLabel(p)}</span></p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs text-gray-700">
                            <span>Name: <b>{`${p.firstName || ''} ${p.middleInitial || ''} ${p.lastName || ''}`.trim() || '—'}</b></span>
                            <span>Age/DOB: <b>{ageDob(p)}</b></span>
                            <span>Gender: <b className="capitalize">{p.gender || '—'}</b></span>
                            <span>Height: <b>{hw(p)}</b></span>
                            <span>Weight: <b>{p.weightLbs ? `${p.weightLbs} lbs` : '—'}</b></span>
                            <span>Smoker: <b>{p.smoker ? 'Yes' : 'No'}</b></span>
                          </div>
                        </div>
                        {q.spouse && (
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs font-bold text-gray-700 mb-1">Spouse</p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs text-gray-700">
                              <span>Name: <b>{`${q.spouse.firstName || ''} ${q.spouse.middleInitial || ''} ${q.spouse.lastName || ''}`.trim() || '—'}</b></span>
                              <span>Age/DOB: <b>{ageDob(q.spouse)}</b></span>
                              <span>Gender: <b className="capitalize">{q.spouse.gender || '—'}</b></span>
                              <span>Height: <b>{hw(q.spouse)}</b></span>
                              <span>Weight: <b>{q.spouse.weightLbs ? `${q.spouse.weightLbs} lbs` : '—'}</b></span>
                              <span>Smoker: <b>{q.spouse.smoker ? 'Yes' : 'No'}</b></span>
                            </div>
                          </div>
                        )}
                        {q.children && q.children.length > 0 && (
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs font-bold text-gray-700 mb-1">Children ({q.children.length})</p>
                            <div className="space-y-0.5 text-xs text-gray-700">
                              {q.children.map((c, i) => <div key={i}>{i + 1}. <b>{`${c.firstName || ''} ${c.middleInitial || ''} ${c.lastName || ''}`.trim() || '—'}</b>{ageDob(c) !== '—' ? <span className="text-gray-500"> · {ageDob(c)}</span> : null}</div>)}
                            </div>
                          </div>
                        )}
                        {q.majorHealthIssues && (
                          <div className="bg-orange-50 border border-orange-100 rounded-lg p-3">
                            <p className="text-xs font-bold text-orange-700 mb-1">Major Health Conditions</p>
                            <p className="text-xs text-gray-700 whitespace-pre-wrap">{q.majorHealthIssues}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                };
                return (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-bold flex items-center gap-2 text-brand-blue">
                        Quote Details
                        {quotes.length > 1 && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold" data-testid="quote-count-badge">{quotes.length} quotes</span>}
                      </h4>
                      {!quoteEditMode && (
                        <button onClick={() => startQuoteEdit(null)} className="flex items-center gap-1.5 text-sm font-semibold text-brand-blue hover:text-brand-orange" data-testid="edit-quote-details-btn">
                          <Calculator size={15} /> {quotes.length === 0 ? 'Add Quote Details' : 'Add Another Quote'}
                        </button>
                      )}
                    </div>
                    {quoteEditMode ? (
                      <div data-testid="quote-details-editor">
                        <p className="text-sm font-semibold text-gray-700 mb-2">{(quoteEditIndex != null && quoteEditIndex < quotes.length) ? `Editing Quote #${quoteEditIndex + 1}` : `New Quote #${quotes.length + 1}`}</p>
                        <AdvancedQuoteFields data={quoteDraft} setData={setQuoteDraft} />
                        <div className="flex gap-2 mt-4">
                          <button onClick={saveQuoteDetails} className="flex-1 bg-brand-orange text-white font-bold py-2.5 rounded-lg hover:bg-orange-600 transition" data-testid="save-quote-details-btn">Save Quote</button>
                          <button onClick={() => setQuoteEditMode(false)} className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50" data-testid="cancel-quote-details-btn">Cancel</button>
                        </div>
                      </div>
                    ) : quotes.length === 0 ? (
                      <p className="text-sm text-gray-400">No quote details captured yet. Click "Add Quote Details" to enrich this contact with full underwriting info.</p>
                    ) : (
                      <div className="space-y-3">
                        {quotes.map(renderQuote)}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>


            {/* Activity Log */}
            <div className="border-t pt-4">
              <h4 className="font-bold mb-3 flex items-center gap-2"><MessageSquare size={16} /> Activity Log</h4>
              <div className="mb-3 flex gap-2">
                <input type="text" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note..." className="flex-1 px-3 py-2 border rounded-md focus:ring-2 focus:ring-teal-500 focus:outline-none text-sm" data-testid="lead-note-input" onKeyDown={(e) => e.key === 'Enter' && handleAddNote(showDetailModal.id)} />
                <button onClick={() => handleAddNote(showDetailModal.id)} className="bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700 text-sm font-semibold" data-testid="add-note-btn">Add</button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {rev(showDetailModal.activityLog).map((entry, i) => (
                  <div key={i} className="text-xs border-l-2 border-teal-300 pl-3 py-1">
                    <span className="font-semibold capitalize">{entry.action}</span>
                    <span className="text-gray-400 ml-2">{new Date(entry.timestamp).toLocaleString()}</span>
                    <p className="text-gray-600 mt-0.5">{entry.detail}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-4 border-t pt-4">
              {(agentInfo?.role === 'admin' || agentInfo?.role === 'super_admin') && (
                <button onClick={() => handleDeleteLead(showDetailModal.id)} className="flex items-center gap-1 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 font-semibold text-sm" data-testid="delete-lead-btn"><Trash2 size={14} /> Delete</button>
              )}
              <button onClick={() => { setShowDetailModal(null); setQuoteEditMode(false); }} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-semibold">Close</button>
            </div>
          </div>
        </div>
      )}
      </div>
    </AgentLayout>
  );
};

export default LeadManagement;
